import React from "react";
import { useNavigate } from "react-router-dom";
import PatientHeader from "./PatientHeader";
import PatientFooter from "./PatientFooter";
import { useWhiteLabel } from "../../contexts/WhiteLabelContext";
import { WhiteLabelButton } from "../../utils/whiteLabelUtils";

const PatientConsents: React.FC = () => {
  const navigate = useNavigate();
  const { primaryColor, isWhiteLabel, contactLink } = useWhiteLabel();

  // Sample consent data - replace with your actual consent documents
  const consents = [
    {
      id: 1,
      title: "Embryo Selection Consent",
      description:
        "Consent for embryo selection and genetic testing procedures",
      lastUpdated: "2025-01-15",
      pdfUrl: "/assets/consents/embryo-selection-consent.pdf",
    },
    {
      id: 2,
      title: "AI Score Analysis Consent",
      description: "Consent for AI-based embryo scoring and analysis",
      lastUpdated: "2025-01-15",
      pdfUrl: "/assets/consents/ai-score-consent.pdf",
    },
    {
      id: 3,
      title: "Data Privacy Consent",
      description: "Consent for data collection, processing, and storage",
      lastUpdated: "2025-01-10",
      pdfUrl: "/assets/consents/privacy-consent.pdf",
    },
    {
      id: 4,
      title: "General Medical Consent",
      description: "General consent for medical procedures and treatments",
      lastUpdated: "2025-01-05",
      pdfUrl: "/assets/consents/general-medical-consent.pdf",
    },
  ];

  const handleViewConsent = (pdfUrl: string) => {
    window.open(pdfUrl, "_blank");
  };

  const handleDownloadConsent = (pdfUrl: string, title: string) => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `${title.replace(/\s+/g, "-").toLowerCase()}.pdf`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PatientHeader isNavigateCheckout={false} itemsCount={0} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header Section */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center hover:text-gray-300 mb-6"
            style={{ color: primaryColor }}
          >
            <svg
              className="h-4 w-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Dashboard
          </button>

          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Consent Documents
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Please review all consent documents before making any selections.
            These documents outline the procedures, risks, and your rights.
          </p>
        </div>

        {/* Important Notice */}
        {/* <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg mb-8">
          <div className="flex items-start">
            <svg
              className="h-6 w-6 text-yellow-600 mt-0.5 mr-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                Important Notice
              </h3>
              <p className="text-yellow-700">
                You will be required to sign these consents before proceeding
                with any selected services. Please take time to read through
                each document carefully and contact us if you have any
                questions.
              </p>
            </div>
          </div>
        </div> */}

        {/* Consent Documents List */}
        <div className="space-y-6">
          {consents.map((consent) => (
            <div
              key={consent.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start flex-col gap-6 sm:flex-row justify-between">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {consent.title}
                  </h3>
                  <p className="text-gray-600 mb-3">{consent.description}</p>
                  <p className="text-sm text-gray-500">
                    Last updated:{" "}
                    {new Date(consent.lastUpdated).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>

                <div className="flex flex-row gap-3 sm:ml-6">
                  <WhiteLabelButton
                    onClick={() => handleViewConsent(consent.pdfUrl)}
                    className="px-3 sm:px-4 py-2 text-white rounded-lg font-medium text-sm"
                  >
                    View Document
                  </WhiteLabelButton>
                  <button
                    onClick={() =>
                      handleDownloadConsent(consent.pdfUrl, consent.title)
                    }
                    className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm"
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section
        <div className="mt-12 bg-blue-50 rounded-xl p-8 text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            Questions About Consents?
          </h3>
          <p className="text-gray-600 mb-6">
            If you have any questions about these consent documents or need
            clarification on any procedures, our team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
              Contact Support
            </button>
            <button className="px-6 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium">
              Schedule Consultation
            </button>
          </div>
        </div> */}

        {/* Back to Dashboard Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate("/patient/dashboard")}
            className="inline-flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            <svg
              className="h-4 w-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Return to Dashboard
          </button>
        </div>
      </div>

      <PatientFooter />
    </div>
  );
};

export default PatientConsents;
